import { OFFICIAL_CRS_SELECTIONS } from '@nowscore/core';
import type { LotteryQuoteDto, LotteryResultDto, MatchDto } from '../api';

export type Bookmaker = 'william' | 'ladbrokes';
export type OddsView = 'compare' | Bookmaker;
export type ParlaySide = 'h' | 'd' | 'a';

export const BOOK_LABELS: Record<Bookmaker, string> = { william: '威廉', ladbrokes: '立博' };
export const MARKET_LABELS: Record<string, string> = { '1x2': '欧指', asian_handicap: '让球盘', totals: '大小球', half_1x2: '半场欧指', half_asian_handicap: '半场让球', half_totals: '半场大小球' };
export const SIDE_LABELS: Record<string, string> = { home: '主', draw: '和', away: '客', over: '大', under: '小' };
export const LOTTERY_MARKET_LABELS: Record<string, string> = { had: '胜平负', hhad: '让球胜平负', ttg: '总进球', crs: '比分', hafu: '半全场' };
export const HAD_SIDE_LABELS: Record<string, string> = { h: '主胜', d: '平局', a: '客胜' };
export const HAFU_SIDE_LABELS: Record<string, string> = {
  hh: '胜胜', hd: '胜平', ha: '胜负',
  dh: '平胜', dd: '平平', da: '平负',
  ah: '负胜', ad: '负平', aa: '负负',
};

export function quotesFor(match: MatchDto, provider: Bookmaker) {
  return (provider === 'ladbrokes' ? match.ladbrokes : match.william) ?? [];
}

export function quoteFor(match: MatchDto, provider: Bookmaker, market: string, side: string) {
  return quotesFor(match, provider).find((item) => item.market === market && item.side === side);
}

export function latestLotteryQuotes(match: MatchDto, market: string): LotteryQuoteDto[] {
  const latest = new Map<string, LotteryQuoteDto>();
  for (const quote of match.lottery?.odds ?? []) {
    if (quote.market !== market) continue;
    const current = latest.get(quote.selection);
    if (!current) { latest.set(quote.selection, quote); continue; }
    const currentTime = Date.parse(current.sourceUpdatedAt ?? current.observedAt);
    const nextTime = Date.parse(quote.sourceUpdatedAt ?? quote.observedAt);
    if (!Number.isFinite(currentTime) || (Number.isFinite(nextTime) && nextTime >= currentTime)) latest.set(quote.selection, quote);
  }
  return [...latest.values()];
}

export function lotteryQuotes(match: MatchDto, market: string): LotteryQuoteDto[] { return latestLotteryQuotes(match, market); }

export function lotteryQuote(match: MatchDto, market: string, selection: string): LotteryQuoteDto | undefined {
  return latestLotteryQuotes(match, market).find((quote) => quote.selection === selection);
}

/** 已完赛场次优先用开奖 SP，未开赛/进行中用法线 SP。 */
export function lotterySettleOdds(match: MatchDto, side: ParlaySide): number | null {
  const combination = side.toUpperCase();
  const settled = (match.lottery?.result ?? []).find((row: LotteryResultDto) => row.pool === 'HAD' && row.combination.toUpperCase() === combination);
  if (settled?.sp && settled.sp > 1) return settled.sp;
  const quote = lotteryQuote(match, 'had', side);
  return quote && quote.odds > 1 ? quote.odds : null;
}

export function lotterySelectionLabel(market: string, selection: string): string {
  if (market === 'had' || market === 'hhad') return HAD_SIDE_LABELS[selection] ?? selection;
  if (market === 'hafu') return HAFU_SIDE_LABELS[selection] ?? selection;
  if (market === 'ttg') {
    const goals = selection.replace(/^s/, '');
    return goals === '7' ? '7+' : goals + ' 球';
  }
  if (market === 'crs') {
    if (selection === 's1sh') return '胜其他';
    if (selection === 's1sd') return '平其他';
    if (selection === 's1sa') return '负其他';
    const parsed = /^s(\d{2})s(\d{2})$/.exec(selection);
    return parsed ? Number(parsed[1]) + ':' + Number(parsed[2]) : selection;
  }
  return selection;
}

export function trendArrow(trend: string | null | undefined): string {
  if (trend === 'up') return '↑';
  if (trend === 'down') return '↓';
  return '';
}

/** 竞彩 SP 隐含概率与返还率（三赔归一化）。 */
export function impliedRates(values: Array<number | null>): { probabilities: number[]; returnRate: number } | null {
  const numbers = values.map((value) => (value && value > 1 ? value : null));
  if (numbers.some((value) => value === null)) return null;
  const inverse = numbers.map((value) => 1 / (value as number));
  const sum = inverse.reduce((total, value) => total + value, 0);
  if (!(sum > 0)) return null;
  return { probabilities: inverse.map((value) => value / sum), returnRate: 1 / sum };
}

function resultSelection(pool: string, combination: string): string | null {
  const normalized = combination.trim().toUpperCase();
  if (pool === 'HAD' || pool === 'HHAD') {
    if (normalized === 'H' || normalized === 'D' || normalized === 'A') return normalized.toLowerCase();
    return null;
  }
  if (pool === 'TTG') {
    const goals = Number(normalized);
    return Number.isFinite(goals) && goals >= 0 ? 's' + Math.min(7, goals) : null;
  }
  if (pool === 'HAFU') {
    const compact = normalized.replace(/[^HDA]/g, '');
    return /^(H|D|A)(H|D|A)$/.test(compact) ? compact.toLowerCase() : null;
  }
  if (pool === 'CRS') {
    const exact = /^(\d+):(\d+)$/.exec(normalized);
    if (exact) {
      const code = 's' + String(Number(exact[1])).padStart(2, '0') + 's' + String(Number(exact[2])).padStart(2, '0');
      if ((OFFICIAL_CRS_SELECTIONS as readonly string[]).includes(code)) return code;
      const home = Number(exact[1]); const away = Number(exact[2]);
      return home > away ? 's1sh' : home < away ? 's1sa' : 's1sd';
    }
  }
  return null;
}

/** 已完赛场次优先使用官方开奖 SP，并映射回归一化赛果编码。 */
export function lotterySettledQuotes(match: MatchDto): LotteryQuoteDto[] {
  const rows = match.lottery?.result ?? [];
  const quotes: LotteryQuoteDto[] = [];
  for (const row of rows) {
    if (!row.sp || row.sp <= 1) continue;
    const pool = row.pool.toUpperCase() as 'HAD' | 'HHAD' | 'TTG' | 'CRS' | 'HAFU';
    if (!['HAD', 'HHAD', 'TTG', 'CRS', 'HAFU'].includes(pool)) continue;
    const selection = resultSelection(pool, row.combination);
    if (!selection) continue;
    quotes.push({
      market: pool.toLowerCase() as LotteryQuoteDto['market'],
      selection,
      odds: row.sp,
      trend: null,
      lineText: pool === 'HHAD' ? row.goalLine : null,
      lineValue: pool === 'HHAD' && row.goalLine != null && Number.isFinite(Number(row.goalLine)) ? Number(row.goalLine) : null,
      observedAt: row.settledAt,
      sourceUpdatedAt: row.settledAt,
      sourceKind: 'sporttery_result',
    });
  }
  return quotes;
}